/** WebGPU device + canvas setup, plus a few tiny resource helpers. */

export interface Gpu {
  /** True when the device can time its own passes. See `Renderer.gpuMs`. */
  timestamps: boolean;
  adapter: GPUAdapter;
  device: GPUDevice;
  canvas: HTMLCanvasElement;
  context: GPUCanvasContext;
  format: GPUTextureFormat;
  width: number;
  height: number;
  dpr: number;
}

export class GpuInitError extends Error {}

export async function initGpu(canvas: HTMLCanvasElement): Promise<Gpu> {
  if (!('gpu' in navigator)) {
    throw new GpuInitError(
      'WebGPU is not available in this browser. ABYSS needs Chrome 113+, Edge 113+, ' +
        'Safari 18+ or Firefox 141+ with hardware acceleration enabled.',
    );
  }
  // ASK MORE THAN ONCE, AND ASK FOR LESS EACH TIME.
  //
  // `powerPreference: 'high-performance'` is specified as a hint, and on a
  // desktop with two GPUs it is the right one. On a phone there is only ever one
  // GPU, and several mobile Chromium builds — Yandex and Samsung Internet among
  // the ones seen failing here — return NULL for a preference they cannot
  // satisfy rather than ignoring it as the spec intends. A single hinted request
  // therefore reports "no WebGPU adapter" on hardware that has a perfectly good
  // one, and the player is told their GPU is blocked when nothing is blocked at
  // all.
  //
  // So: the hint first, then no hint, then the software fallback. Which rung
  // answered is recorded, because "it needed the unhinted request" is the fact
  // that explains a whole class of phone reports.
  let adapter: GPUAdapter | null = null;
  let rung = 'high-performance';
  try {
    adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
  } catch { /* some builds throw rather than resolving null */ }
  if (!adapter) {
    rung = 'default';
    try {
      adapter = await navigator.gpu.requestAdapter();
    } catch { /* fall through to the fallback adapter */ }
  }
  if (!adapter) {
    rung = 'fallback';
    try {
      adapter = await navigator.gpu.requestAdapter({ forceFallbackAdapter: true });
    } catch { /* nothing left to try */ }
  }
  if (!adapter) {
    throw new GpuInitError(
      'No WebGPU adapter found. The API is present but this browser would not '
        + 'hand over a GPU — on Android that is usually the browser rather than '
        + 'the phone.',
    );
  }
  if (rung !== 'high-performance') console.info(`[abyss] adapter via ${rung} request`);
  (window as unknown as { __ABYSS_ADAPTER?: string }).__ABYSS_ADAPTER = rung;

  /**
   * Ask for GPU timing if the adapter will give it.
   *
   * Optional on purpose — `timestamp-query` is widely but not universally
   * available, and it must never be the difference between the game running
   * and not. Without it the readout simply says so.
   */
  const wantTimestamps = adapter.features.has('timestamp-query');
  const device = await adapter.requestDevice({
    requiredFeatures: wantTimestamps ? ['timestamp-query'] : [],
    requiredLimits: {
      maxStorageBufferBindingSize: Math.min(
        adapter.limits.maxStorageBufferBindingSize,
        128 * 1024 * 1024,
      ),
    },
  });
  device.lost.then((info) => {
    console.error('[abyss] GPU device lost:', info.reason, info.message);
  });

  const context = canvas.getContext('webgpu');
  if (!context) throw new GpuInitError('Could not acquire a WebGPU canvas context.');

  const format = navigator.gpu.getPreferredCanvasFormat();
  const gpu: Gpu = {
    adapter, device, canvas, context, format,
    timestamps: wantTimestamps,
    width: 1, height: 1, dpr: 1,
  };
  configure(gpu);
  return gpu;
}

/**
 * Cap on rendered pixels. The scene target is MSAA rgba16float and the composite
 * ray-marches per pixel, so on a 2x HiDPI display we would otherwise be drawing
 * 7 MP — 3.5x the 1080p budget in PRD §26 for no visible gain over the 4x MSAA
 * we already have.
 */
let maxPixels = 2_400_000;

export function setPixelBudget(px: number) {
  maxPixels = Math.max(200_000, px);
}

export function configure(gpu: Gpu): boolean {
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cw = gpu.canvas.clientWidth, chh = gpu.canvas.clientHeight;
  const over = (cw * chh * dpr * dpr) / maxPixels;
  if (over > 1) dpr /= Math.sqrt(over);
  const w = Math.max(1, Math.round(cw * dpr));
  const h = Math.max(1, Math.round(chh * dpr));
  if (w === gpu.width && h === gpu.height && dpr === gpu.dpr) return false;
  gpu.canvas.width = w;
  gpu.canvas.height = h;
  gpu.width = w;
  gpu.height = h;
  gpu.dpr = dpr;
  gpu.context.configure({
    device: gpu.device,
    format: gpu.format,
    alphaMode: 'opaque',
    // COPY_DST because the temporal resolve writes its own texture — that
    // result is both the frame and the next frame's history — and then copies
    // it here rather than drawing it a second time.
    usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_DST,
  });
  return true;
}

export function makeBuffer(
  device: GPUDevice,
  data: Float32Array<ArrayBuffer> | Uint32Array<ArrayBuffer> | Uint16Array<ArrayBuffer>,
  usage: GPUBufferUsageFlags,
  label?: string,
): GPUBuffer {
  const size = Math.ceil(data.byteLength / 4) * 4;
  const buf = device.createBuffer({ size, usage: usage | GPUBufferUsage.COPY_DST, label });
  device.queue.writeBuffer(buf, 0, data as GPUAllowSharedBufferSource);
  return buf;
}

export function makeStorage(device: GPUDevice, bytes: number, label?: string): GPUBuffer {
  return device.createBuffer({
    size: Math.ceil(bytes / 16) * 16,
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC,
    label,
  });
}

export function makeUniform(device: GPUDevice, bytes: number, label?: string): GPUBuffer {
  return device.createBuffer({
    size: Math.ceil(bytes / 16) * 16,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    label,
  });
}

/**
 * SHOUT WHEN A SHADER DOES NOT COMPILE.
 *
 * WGSL is a string to everything upstream of the GPU: the bundler does not
 * parse it, `tsc` does not see it, and `npm run verify` never touches it. A
 * pipeline built from a module that failed to compile is simply invalid, and an
 * invalid pipeline draws NOTHING — so a one-character scope error arrives as a
 * black screen with a working HUD on top of it, which looks like a dozen other
 * problems and points at none of them. That exact bug shipped, and the only
 * reason it was caught is that someone was looking at the game.
 *
 * `getCompilationInfo` is the one place the GPU will say what is wrong. It
 * resolves after construction, so this cannot block the frame — but it can put
 * the message in the console and on the boot screen, with the offending line,
 * which turns a silent black frame into a report worth having.
 */
export function checkShader(mod: GPUShaderModule, label: string, source: string) {
  void mod.getCompilationInfo().then((info) => {
    const bad = info.messages.filter((m) => m.type === 'error');
    if (!bad.length) return;
    const lines = source.split('\n');
    for (const m of bad) {
      const at = lines[m.lineNum - 1] ?? '';
      console.error(`[abyss] ${label}.wgsl:${m.lineNum}:${m.linePos}  ${m.message}\n    ${at.trim()}`);
    }
    const first = bad[0];
    const boot = document.getElementById('boot');
    if (boot) {
      boot.classList.remove('hidden');
      boot.innerHTML = `<h1>ABYSS</h1><p class="err">SHADER FAILED TO COMPILE</p>`
        + `<p class="diag">${label}.wgsl line ${first.lineNum}</p>`
        + `<p class="diag">${String(first.message).replace(/[<>&]/g, '')}</p>`;
    }
  });
}
